<template>
    <div class="semi-finished-product-deliver-overview-page">
        <div class="card products-list">
            <div class="card-content">
                <crm-table
                    ref="crmTable"
                    :columns="columns"
                    :items="products"
                    :count="products.length + 1"
                    :status="status"
                    @fetch="onFetchDebounced"
                    :default-filters="clearedFilters"
                    default-sort="createdAt"
                    default-order="desc"
                    :has-sticky="true"
                    class="delivered-semifinished-products"
                    :use-global-scroll="true"
                    :active-id="0"
                >
                    <!-- FILTERS -->
                    <template v-slot:filter-producttypeName="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="productTypeOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-profileGroupName="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="profileGroupOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-profileSubgroupName="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="profileSubGroupOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-supplierName="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="supplierOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-stockLocation1="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="stockLocationOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-stockLocation2="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="stockLocationOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-stockLocation3="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="stockLocationOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-specialFilter="props">
                        <auto-complete-input
                            class="filter"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="specialFilterOptions"
                            :placeholder="props.column.label"
                        />
                    </template>

                    <template v-slot:filter-clearFilters="props">
                        <ui5-button @click="clearFilters">
                            {{
                                trans(
                                    'pages.semifinishedproductdeliveroverview.reset'
                                )
                            }}
                        </ui5-button>
                    </template>

                    <!-- SELECTION -->
                    <template v-slot:selection="props">
                        <td>
                            <div class="checkbox">
                                <input
                                    v-model="props.item.selected"
                                    type="checkbox"
                                    :name="'selection-' + props.item.productId"
                                    :true-value="true"
                                    :false-value="false"
                                />
                                <label
                                    :for="'selection-' + props.item.productId"
                                ></label>
                            </div>
                        </td>
                    </template>

                    <!-- ACTIONS -->
                    <template v-slot:actions="props">
                        <td>
                            <template v-for="rowAction in rowActions">
                                <div
                                    class="row-action"
                                    :class="{
                                        hidden:
                                            rowAction.condition &&
                                            !rowAction.condition(props.item),
                                    }"
                                    :key="rowAction.label + props.item.ID"
                                    v-on:click.stop.prevent="
                                        rowAction.onClick(props.item)
                                    "
                                >
                                    <img
                                        v-if="rowAction.icon && !rowAction.line"
                                        class="icon"
                                        :title="rowAction.label"
                                        :alt="rowAction.label"
                                        :src="rowAction.icon"
                                    />

                                    <div
                                        class="no-icon"
                                        v-else-if="!rowAction.line"
                                    >
                                        {{ rowAction.label }}
                                    </div>
                                    <div
                                        class="line"
                                        v-else
                                    >
                                        &nbsp;
                                    </div>
                                </div>
                            </template>
                        </td>
                    </template>

                    <!-- CELLS -->
                    <template v-slot:stockLocation1="props">
                        <td class="column-stockLocation">
                            <auto-complete-input
                                v-if="stockLocations"
                                :value="props.item.stockLocations[0]"
                                @input="
                                    updateStockLocation(props.item, 0, $event);
                                    updateBoxNrAndColliForProduct(props.item);
                                "
                                :options="stockLocationOptions"
                                :initial-search="
                                    getInitialSearch(
                                        props.item.stockLocations[0]
                                    )
                                "
                                :placeholder="
                                    trans(
                                        'pages.semifinishedproductdeliveroverview.location_1'
                                    )
                                "
                            />
                        </td>
                    </template>

                    <template v-slot:stockLocation2="props">
                        <td class="column-stockLocation">
                            <auto-complete-input
                                v-if="stockLocations"
                                :value="props.item.stockLocations[1]"
                                @input="
                                    updateStockLocation(props.item, 1, $event);
                                    updateBoxNrAndColliForProduct(props.item);
                                "
                                :options="stockLocationOptions"
                                :placeholder="
                                    trans(
                                        'pages.semifinishedproductdeliveroverview.location_2'
                                    )
                                "
                            />
                        </td>
                    </template>

                    <template v-slot:stockLocation3="props">
                        <td class="column-stockLocation">
                            <auto-complete-input
                                v-if="stockLocations"
                                :value="props.item.stockLocations[2]"
                                @input="
                                    updateStockLocation(props.item, 2, $event);
                                    updateBoxNrAndColliForProduct(props.item);
                                "
                                :options="stockLocationOptions"
                                :placeholder="
                                    trans(
                                        'pages.semifinishedproductdeliveroverview.location_3'
                                    )
                                "
                            />
                        </td>
                    </template>

                    <template v-slot:colli="props">
                        <td class="column-colli">
                            <text-input
                                v-model="props.item.colli"
                                :placeholder="
                                    trans(
                                        'pages.semifinishedproductdeliveroverview.colli'
                                    )
                                "
                                @blur="
                                    updateBoxNrAndColliForProduct(props.item)
                                "
                            />
                        </td>
                    </template>

                    <!-- FOOTER -->
                    <template
                        v-slot:footer
                        v-if="status === 'success'"
                    >
                        <tr>
                            <th class="sticky">
                                <div class="multiselect-actions">
                                    <div class="checkbox">
                                        <input
                                            id="select-all"
                                            @change="selectAll"
                                            type="checkbox"
                                            name="select-all"
                                            :true-value="true"
                                            :false-value="false"
                                        />
                                        <label for="select-all"></label>
                                    </div>
                                    <div
                                        v-if="selectedProducts.length > 0"
                                        class="multiselect-action"
                                        @click="$refs.popup.showPopup()"
                                    >
                                        <i class="fa fa-cogs" />
                                        {{
                                            trans(
                                                'pages.semifinishedproductdeliveroverview.process'
                                            )
                                        }}
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </template>
                </crm-table>
            </div>
        </div>

        <delivery-semifinished-product-dialog
            :first-selected-product="firstSelectedProduct"
            :update-box-nr-and-colli-for-product="updateBoxNrAndColliForProduct"
            :print-semifinished-product-label="printSemifinishedProductLabel"
            :stock-location-options="stockLocationOptions"
            ref="popup"
        />
    </div>
</template>

<script>
import CrmTable from '../components/CrmTable';
import productApi from '../api/product';
import TextInput from '../components/TextInput';
import orderApi from '../api/order';
import supplierApi from '../api/supplier';
import productTypeApi from '../api/productType';
import stockLocationApi from '../api/stockLocation';
import AutoCompleteInput from '../components/AutoCompleteInput';
import axios from 'axios';
import {
    clearedFilters,
    specialFilterOptions,
} from '../shared/semiFinishedProductDeliverOverview';
import exportApi from '../api/export';
import DeliverySemifinishedProductDialog from '../components/semiFinishedProduct/DeliverySemifinishedProductDialog';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import { isNumber } from '../helpers/GeneralHelper';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import _ from 'lodash';
import localization from '../mixins/localization';

export default {
    mixins: [NavigationFunctionsMixin, localization],
    data() {
        return {
            onFetchDebounced: _.debounce(this.onFetch, 500),
            clearedFilters: { ...clearedFilters },
            specialFilterOptions,
            status: 'running',
            hasNoMore: false,
            productTypes: [],
            suppliers: [],
            groups: [],
            subGroups: [],
            stockLocations: [],
            products: [],
            rowActions: [
                {
                    icon: 'images/icons/info-circle.svg',
                    label: 'info',
                    onClick: (item) => {
                        this.goToProductPage(item.productId);
                    },
                },
                {
                    icon: 'images/icons/print.svg',
                    label: 'print',
                    onClick: (item) => {
                        this.printSemifinishedProductLabel(item.productId);
                    },
                },
            ],
            columns: [
                {
                    name: 'selection',
                    label: '',
                },
                {
                    name: 'actions',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.actions'
                    ),
                },
                {
                    name: 'productNr',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.product_nr'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'prositeId',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.prosite_nr'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'orderId',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.order_nr'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'customerName',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.customer'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'reference',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.reference'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'producttypeName',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.type'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'profileGroupName',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.group'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'profileSubgroupName',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.subgroup'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'supplierName',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.supplier'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'dimensions',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.dimensions'
                    ),
                    searchable: true,
                },
                {
                    name: 'productionDate',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.production_date'
                    ),
                    searchable: true,
                },
                {
                    name: 'colli',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.colli'
                    ),
                    searchable: true,
                },
                {
                    name: 'stockLocation1',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.stock_location_1'
                    ),
                    searchable: true,
                },
                {
                    name: 'stockLocation2',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.stock_location_2'
                    ),
                    searchable: true,
                },
                {
                    name: 'stockLocation3',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.stock_location_3'
                    ),
                    searchable: true,
                },
                {
                    name: 'specialFilter',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.filter'
                    ),
                    searchable: true,
                    hideColumn: true,
                },
                {
                    name: 'clearFilters',
                    label: this.trans(
                        'pages.semifinishedproductdeliveroverview.clear_filters'
                    ),
                    searchable: true,
                    hideColumn: true,
                },
            ],
            sortColumns: {
                productNr: 'productnr',
                prositeId: 'prositenr',
                orderId: 'ordernr',
                customerName: 'customer',
                reference: 'reference',
                producttypeName: 'producttype',
                profileGroupName: 'productgroup',
                profileSubgroupName: 'productsubgroup',
                supplierName: 'supplier',
            },
        };
    },
    computed: {
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        stockLocationOptions() {
            return this.stockLocations
                ? [
                      { label: '-----', value: 0 },
                      ...this.stockLocations.map((x) => {
                          return { value: x.ID, label: x.label };
                      }),
                  ]
                : [];
        },
        productTypeOptions() {
            return [
                { label: '-----', value: 0 },
                ...this.productTypes.map((type) => ({
                    value: type.producttypeId,
                    label: type.label,
                })),
            ];
        },
        profileGroupOptions() {
            return [
                { label: '-----', value: 0 },
                ...this.groups.map((type) => ({
                    value: type.id,
                    label: type.name,
                })),
            ];
        },
        profileSubGroupOptions() {
            return [
                { label: '-----', value: 0 },
                ...this.subGroups.map((type) => ({
                    value: type.id,
                    label: type.name,
                })),
            ];
        },
        supplierOptions() {
            return [
                { label: '-----', value: 0 },
                ...this.suppliers.map((type) => ({
                    value: type.id,
                    label: type.name,
                })),
            ];
        },
        selectedProducts() {
            if (!this.products) {
                return [];
            }

            return this.products.filter((x) => x.selected);
        },
        firstSelectedProduct() {
            return this.selectedProducts.length
                ? this.selectedProducts[0]
                : null;
        },
    },
    methods: {
        clearFilters() {
            this.$refs.crmTable.clearFilters();
        },
        updateStockLocation(item, index, $event) {
            const stockLocations = item.stockLocations;
            stockLocations[index] = $event;
            item.stockLocations = [...stockLocations];
        },
        getInitialSearch(value) {
            const option = this.stockLocationOptions.find(
                (x) => x.value == value
            );
            return option ? option.label : '';
        },
        selectAll($event) {
            const value = $event.target.checked;
            this.products.forEach((x) => {
                x.selected = value;
            });
        },
        onFetch(filters) {
            if (!filters.more) {
                this.hasNoMore = false;
            }

            if (this.hasNoMore) {
                return;
            }

            if (filters.page === 1) {
                this.products = [];
            }
            this.status = 'running';

            productApi
                .overviewDeliveredSemifinishedProducts({
                    sortField: this.sortColumns[filters.sort],
                    sortOrder: filters.order === 'asc' ? 'ASC' : 'DESC',
                    productnr: filters.productNr,
                    prositenr: filters.prositeId,
                    ordernr: filters.orderId,
                    customer: filters.customerName,
                    reference: filters.reference,
                    producttype: filters.producttypeName,
                    productgroup: filters.profileGroupName,
                    productsubgroup: filters.profileSubgroupName,
                    supplier: filters.supplierName,
                    dimensions: filters.dimensions,
                    productionDate: filters.productionDate,
                    stockLocation1: filters.stockLocation1,
                    stockLocation2: filters.stockLocation2,
                    stockLocation3: filters.stockLocation3,
                    colli: filters.colli,
                    specialFilter: filters.specialFilter,
                    offset: this.products ? this.products.length : 0,
                })
                .then((response) => {
                    const products = response.data.data;
                    if (!this.products) {
                        this.products = [];
                    }
                    this.products = this.products.concat(products);
                    this.status = 'success';

                    if (products.length < 50) {
                        this.hasNoMore = true;
                    }
                })
                .catch((err) => {
                    if (!axios.isCancel(err)) {
                        this.status = 'error';
                    }
                });
        },
        printSemifinishedProductLabel(productId) {
            KeyHelper.handlePrintOrExport(
                () => exportApi
                    .printProductionLabel(productId, this.selectedPrinterPostParameter)
                    .then(() => {
                        showInfoMessage(
                            this.trans(
                                'pages.semifinishedproductdeliveroverview.label_printed',
                                { productId: productId }
                            )
                        );
                    }),
                Config.url + '/export/exportProductionLabel/?productId=' + productId
            );
        },
        updateBoxNrAndColliForProduct(product) {
            if (
                product.colli !== '' &&
                isNumber(product.colli) &&
                product.colli > 0
            ) {
                productApi.setAdditionalInfoToProduct(
                    product.productId,
                    product.boxnr,
                    product.colli,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    product.stockLocations
                );
                showInfoMessage(
                    this.trans(
                        'pages.semifinishedproductdeliveroverview.boxnr_colli_updated'
                    )
                );
            } else {
                showErrorMessage(
                    this.trans(
                        'pages.semifinishedproductdeliveroverview.colli_greater_than_zero'
                    )
                );
            }
        },
    },
    mounted() {
        orderApi.productTypesForOrder().then((response) => {
            this.productTypes = response.data.data;
        });

        supplierApi.fetchAllSuppliers().then((response) => {
            this.suppliers = response.data.data;
        });

        productTypeApi.listGroups().then((response) => {
            this.groups = response.data.data;
        });

        productTypeApi.listSubgroups().then((response) => {
            this.subGroups = response.data.data;
        });

        stockLocationApi.fetchAll().then((response) => {
            this.stockLocations = response.data.data;
        });
    },
    components: {
        CrmTable,
        TextInput,
        AutoCompleteInput,
        DeliverySemifinishedProductDialog,
    },
};
</script>
